import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, IndianRupee } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api';

const inputCls = 'w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rozops-blue/20 focus:border-rozops-blue';

export default function Payments() {
  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ customer_id: '', invoice_id: '', amount: 0, payment_method: 'cash', notes: '' });

  const load = async () => {
    const [p, c, i] = await Promise.all([apiGet('/merchant/payments'), apiGet('/merchant/customers'), apiGet('/merchant/invoices')]);
    setList(p.data || []);
    setCustomers(c.data || []);
    setInvoices(i.data || []);
  };
  useEffect(() => { load().catch(e => toast.error(e.message)); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await apiPost('/merchant/payments', { ...form, amount: Number(form.amount), invoice_id: form.invoice_id || null });
      toast.success('Payment recorded');
      setShowAdd(false);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const custInvoices = invoices.filter(i => i.customer_id === form.customer_id && Number(i.due_amount) > 0);

  return (
    <div className="space-y-5" data-testid="payments-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading font-extrabold text-3xl text-rozops-navy tracking-tight">Payments</h1>
        <button onClick={() => setShowAdd(true)} data-testid="add-payment-btn" className="bg-rozops-green hover:bg-rozops-green-dark text-white rounded-xl px-4 py-2.5 font-semibold shadow-soft inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Record payment</button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-soft overflow-x-auto">
        <table className="w-full">
          <thead className="bg-rozops-bg border-b border-gray-100">
            <tr className="text-left text-xs uppercase tracking-wider text-rozops-text-muted font-bold">
              <th className="py-3 px-6">Date</th><th className="py-3 px-6">Customer</th><th className="py-3 px-6">Amount</th><th className="py-3 px-6">Method</th><th className="py-3 px-6">Ref</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-rozops-text-muted">No payments yet.</td></tr>}
            {list.map(p => (
              <tr key={p.id} className="border-b border-gray-50" data-testid={`payment-${p.id}`}>
                <td className="py-4 px-6 text-sm">{(p.payment_date || '').slice(0, 10)}</td>
                <td className="py-4 px-6 font-semibold">{p.customer?.full_name}</td>
                <td className="py-4 px-6 text-rozops-green font-heading font-bold">₹{Number(p.amount).toFixed(2)}</td>
                <td className="py-4 px-6 text-sm"><span className="px-2.5 py-1 rounded-full bg-rozops-blue-light text-rozops-blue text-xs font-bold">{p.payment_method}</span></td>
                <td className="py-4 px-6 text-xs text-rozops-text-muted">{p.transaction_reference || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={() => setShowAdd(false)}>
          <div className="bg-white rounded-2xl shadow-medium p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading font-bold text-xl text-rozops-navy mb-4">Record payment</h2>
            <form onSubmit={add} className="space-y-3">
              <select required className={inputCls} value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value, invoice_id: '' })} data-testid="pay-customer">
                <option value="">Select customer</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
              </select>
              {custInvoices.length > 0 && (
                <select className={inputCls} value={form.invoice_id} onChange={(e) => setForm({ ...form, invoice_id: e.target.value })}>
                  <option value="">— Skip / no invoice —</option>
                  {custInvoices.map(i => <option key={i.id} value={i.id}>{i.invoice_number} (due ₹{Number(i.due_amount).toFixed(2)})</option>)}
                </select>
              )}
              <input required type="number" step="0.01" placeholder="Amount" className={inputCls} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="pay-amount" />
              <select className={inputCls} value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
                <option value="cash">Cash</option><option value="upi">UPI</option><option value="bank_transfer">Bank transfer</option><option value="card">Card</option><option value="razorpay">Razorpay (mock)</option>
              </select>
              <input placeholder="Notes / reference" className={inputCls} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2 rounded-xl text-rozops-text-muted">Cancel</button>
                <button type="submit" data-testid="pay-submit" className="bg-rozops-green text-white rounded-xl px-4 py-2 font-semibold">Record</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
